import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import { getBrandingBannerUrl } from '@/services/tenant/tenantImages';
import { translateDynamicContent } from '@/i18n';
import styles from './HomeBanner.module.css';

/**
 * HomeBanner — tenant-configured hero banner.
 * Mirrors Flutter WebCareerPage._buildBannerImage().
 *
 * Banner image source priority (Flutter _buildBannerImage):
 * 1. desktopBanner / mobileBanner imageUrl from tenant preferences
 * 2. NetworkImageConstant.homeHeaderImage — header background from content settings,
 *    else the tenant default <image host>/<workspaceId>/banner_image.jpg
 *
 * Sizing mirrors GenericHelpers.convertStrHeightToDouble / convertStrWidthToDouble, getBoxFit
 * and getAlignment: no configured height → the image keeps its natural aspect ratio (fitWidth).
 *
 * Clickable regions (homeBannerImageActionsDesktop/Mobile) mirror Flutter ImageMap: coordinates
 * are natural-image pixels, scaled to the banner box (ImageMap maps taps by imageSize / boxSize).
 * Preference `homeBannerActionHotspotIndicators` paints them red (Flutter ImageMapPainter fill).
 *
 * The tagline (WebCareerPageConstants.bannerText = contentSettings.header.content, through the
 * language file's `dynamicContent` map) overlays the bottom centre: white, shadowed, max 3 lines.
 * A dark gradient behind it keeps the white text readable on bright banner images (Flutter has
 * only the text shadow); the image itself is untouched above it.
 */

interface BannerAction {
  actionType?: string;
  actionData?: string;
  coordinates?: {
    x1?: number;
    y1?: number;
    width?: number;
    height?: number;
  };
}

/** Flutter convertStrHeightToDouble: 'px' → px, '%' / 'h' → % of the screen height, bare number → px. */
function toCssHeight(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value);
  const num = parseFloat(text);
  if (isNaN(num)) return undefined;
  if (text.includes('px')) return `${num}px`;
  if (text.includes('%') || text.includes('h')) return `${num}vh`;
  return `${num}px`;
}

/** Flutter convertStrWidthToDouble: 'px' → px, '%' / 'w' → % of the screen width, bare number → px. */
function toCssWidth(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value);
  const num = parseFloat(text);
  if (isNaN(num)) return undefined;
  if (text.includes('px')) return `${num}px`;
  if (text.includes('%') || text.includes('w')) return `${num}vw`;
  return `${num}px`;
}

/** Flutter getAlignment (unknown values, including topCenter, fall back to center). */
const OBJECT_POSITION: Record<string, string> = {
  topLeft: 'left top',
  topRight: 'right top',
  bottomLeft: 'left bottom',
  bottomRight: 'right bottom',
  centerLeft: 'left center',
  centerRight: 'right center',
  center: 'center',
};

/** Flutter getBoxFit (default contain). fitWidth / fitHeight fill a fixed-height banner. */
const OBJECT_FIT: Record<string, CSSProperties['objectFit']> = {
  contain: 'contain',
  cover: 'cover',
  fill: 'fill',
  none: 'none',
  scaleDown: 'scale-down',
  fitWidth: 'cover',
  fitHeight: 'cover',
};

function useBannerConfig(isMobile: boolean) {
  const preferences = useTenantStore((s) => s.preferences);
  const mobilePrefs = useTenantStore((s) => s.mobilePreferences);
  const content = useTenantStore((s) => s.content);

  return useMemo(() => {
    const prefs = (isMobile ? mobilePrefs : preferences) ?? {};
    const bannerConfig =
      ((prefs as Record<string, unknown>)[isMobile ? 'mobileBanner' : 'desktopBanner'] as
        | Record<string, unknown>
        | undefined) ?? {};

    const imageUrl = (bannerConfig.imageUrl as string) || getBrandingBannerUrl(content) || '';
    const fit = (bannerConfig.fit as string) || 'fitWidth';

    const actionsKey = isMobile
      ? 'homeBannerImageActionsMobile'
      : 'homeBannerImageActionsDesktop';
    const rawActions = (prefs as Record<string, unknown>)[actionsKey] as
      | BannerAction[]
      | undefined;

    return {
      imageUrl,
      height: toCssHeight(bannerConfig.height),
      width: toCssWidth(bannerConfig.width),
      objectFit: OBJECT_FIT[fit] ?? 'contain',
      objectPosition: OBJECT_POSITION[(bannerConfig.alignment as string) ?? ''] ?? 'center',
      actions: rawActions ?? [],
      // Flutter reads this from the tenant (desktop) preferences at every width
      showHotspots: !!(preferences as Record<string, unknown> | null)?.homeBannerActionHotspotIndicators,
      tagline: content?.contentSettings?.header?.content ?? '',
    };
  }, [isMobile, preferences, mobilePrefs, content]);
}

/** Flutter WebCareerPage._handleCoordinatesAction */
function handleBannerAction(action: BannerAction, navigate: NavigateFunction) {
  const type = (action.actionType ?? '').toLowerCase();
  const data = action.actionData ?? '';
  if (!data) return;

  switch (type) {
    case 'url_launch':
    case 'url_lunch':
      window.open(data, '_blank', 'noopener');
      break;
    case 'navigation':
    case 'navigate':
      // Flutter Navigator.pushNamed(context, actionData) — an in-app route
      if (/^https?:\/\//i.test(data)) window.location.href = data;
      else navigate(data.startsWith('/') ? data : `/${data}`);
      break;
    case 'mailto':
      window.location.href = `mailto:${data}`;
      break;
    default:
      break;
  }
}

/** Accessible name for an invisible hotspot: where it goes (site and path, email, or in-app route). */
function bannerActionLabel(action: BannerAction): string {
  const data = (action.actionData ?? '').trim();
  if (!data) return 'Banner link';
  try {
    const url = new URL(data);
    return `${url.hostname}${url.pathname === '/' ? '' : url.pathname}`;
  } catch {
    return data;
  }
}

interface HomeBannerProps {
  isMobile: boolean;
}

export function HomeBanner({ isMobile }: HomeBannerProps) {
  const config = useBannerConfig(isMobile);
  const navigate = useNavigate();
  // Re-renders on language change so the tagline picks up the new dynamicContent map
  useTranslation();
  const tagline = config.tagline ? translateDynamicContent(config.tagline) : '';

  // Natural image size — hotspot coordinates are in image pixels (Flutter ImageInfo.image.width/height)
  const [loaded, setLoaded] = useState<{ url: string; width: number; height: number } | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const natural = loaded?.url === config.imageUrl ? loaded : null;
  const isLoaded = !!natural;
  const hasImage = !!config.imageUrl && failedUrl !== config.imageUrl;

  // Nothing to show without a usable image
  if (!hasImage) return null;

  // Natural height (Flutter fitWidth with no height); a placeholder height until the image loads
  const isNaturalHeight = !config.height && hasImage;
  const placeholderHeight = isMobile ? '200px' : '300px';

  return (
    <div
      className={styles.banner}
      style={{
        height: config.height ?? (isNaturalHeight && isLoaded ? undefined : placeholderHeight),
        width: config.width,
      }}
    >
      {hasImage && (
        <img
          src={config.imageUrl}
          alt=""
          className={`${styles.bannerImage} ${isNaturalHeight ? styles.bannerImageNatural : ''}`}
          style={isNaturalHeight ? undefined : { objectFit: config.objectFit, objectPosition: config.objectPosition }}
          onLoad={(e) =>
            setLoaded({
              url: config.imageUrl,
              width: e.currentTarget.naturalWidth,
              height: e.currentTarget.naturalHeight,
            })
          }
          onError={() => setFailedUrl(config.imageUrl)}
        />
      )}

      {/* Clickable regions — active once the image size is known, like Flutter ImageMap */}
      {natural &&
        natural.width > 0 &&
        natural.height > 0 &&
        config.actions.map((action, idx) => {
          const coords = action.coordinates;
          if (!coords) return null;
          const pct = (value: number | undefined, size: number) => `${((value ?? 0) / size) * 100}%`;
          return (
            <button
              key={idx}
              className={`${styles.hotspot} ${config.showHotspots ? styles.hotspotIndicator : ''}`}
              style={{
                left: pct(coords.x1, natural.width),
                top: pct(coords.y1, natural.height),
                width: pct(coords.width, natural.width),
                height: pct(coords.height, natural.height),
              }}
              onClick={() => handleBannerAction(action, navigate)}
              aria-label={bannerActionLabel(action)}
            />
          );
        })}

      {/* Flutter WebCareerPage banner text overlay, over a readability scrim */}
      {tagline && <div className={styles.taglineScrim} aria-hidden="true" />}
      {tagline && (
        <p className={`${styles.tagline} ${isMobile ? styles.taglineMobile : ''}`}>{tagline}</p>
      )}
    </div>
  );
}
